import { z } from "zod"

import {
  branchIdSchema,
  commandIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

export const financeConsoleCommandTypeV1Schema = z.enum([
  "finance.configuration.configure",
  "finance.posting.post",
  "finance.posting.reverse",
  "finance.receivable.administer",
  "finance.payable.administer",
  "transaction_tax.review.resolve",
  "finance.period.preview",
  "finance.period.close",
  "finance.period.reopen_preview",
  "finance.period.reopen",
  "finance.late_adjustment.post",
  "finance.correction.post",
  "stripe.accounting.reconcile",
  "cutover.batch.create",
  "cutover.record.stage",
  "cutover.batch.validate",
  "cutover.batch.approve",
  "cutover.master_active.commit",
  "cutover.open_finance.preview",
  "cutover.open_finance.commit",
  "cutover.inventory_opening.preview",
  "cutover.inventory_opening.commit",
])

const entityTypeSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/)
const idempotencyKeySchema = z.string().regex(
  /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/,
)

export const financeConsolePeriodReopenDraftPayloadV1Schema = z.object({
  periodId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  previewFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  reason: z.string().trim().min(12).max(500),
  sandboxStepUpConfirmed: z.literal(true),
}).strict()

/**
 * Actor and Tenant are deliberately absent. The BFF derives both from the current
 * Membership and refuses scope not present in that Membership before forwarding.
 * The command payload remains the already-versioned product command payload and is
 * revalidated by the authoritative Frappe Domain before commit.
 */
export const financeConsoleCommandDraftV1Schema = z.object({
  requestVersion: z.literal(1),
  commandId: commandIdSchema,
  idempotencyKey: idempotencyKeySchema,
  correlationId: correlationIdSchema,
  commandType: financeConsoleCommandTypeV1Schema,
  commandVersion: z.literal(1),
  expectedVersion: z.number().int().nonnegative().safe(),
  context: z.object({
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema.nullable(),
    entityType: entityTypeSchema,
    entityId: entityIdSchema,
  }).strict(),
  payload: z.record(z.string(), z.json()),
}).strict().superRefine((value, context) => {
  if (Object.keys(value.payload).length > 100) {
    context.addIssue({
      code: "custom",
      path: ["payload"],
      message: "Finance command payload is too large.",
    })
  }
})

export type FinanceConsoleCommandTypeV1 = z.infer<
  typeof financeConsoleCommandTypeV1Schema
>
export type FinanceConsoleCommandDraftV1 = z.infer<
  typeof financeConsoleCommandDraftV1Schema
>
